import { useState, useEffect } from "react";
import { Eye } from "lucide-react";
import MaterialTable from "material-table";
import { materialTableES } from "../../../../services/materialTableES";
import { ProductStatusBadge, getProductStatus } from "../ProductStatusBadge";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import styles from "./ProductsTable.module.scss";
import { ProductDetails } from "../ProductDetails/ProductDetails";
import { useGetAllProducts } from "../../../../utils/react-query/queryEcommerce/queries";
import useCompanyStore from "../../../../Store/supplierCompany/companyData";
import { useFilterStore } from "../../../../supplierEcommerce/store";
import { type SalesProduct } from "./types";

interface ProductsTableProps {
  searchQuery: string;
  filters: any;
}

export function ProductsTable({ searchQuery, filters }: ProductsTableProps) {
  const { companyName } = useCompanyStore();
  const setPrice = useFilterStore((state) => state.setPrice);
  const { data: productsData, isLoading } = useGetAllProducts(
    companyName || ""
  );
  const [data, setData] = useState<SalesProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<SalesProduct | null>(
    null
  );
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  useEffect(() => {
    if (filters?.priceRange) {
      const [minPrice, maxPrice] = filters.priceRange.split(",").map(Number);
      if (!isNaN(minPrice) && !isNaN(maxPrice)) {
        setPrice([minPrice, maxPrice]);
      }
    }
  }, [filters?.priceRange, setPrice]);

  useEffect(() => {
    if (productsData?.products && productsData.products.length > 0) {
      const mappedProducts = productsData.products.map((product: any) => ({
        // SupplierProduct base fields
        product_id: product.product_id ?? 0,
        cart_item_id: 0,
        quantity: 1,
        name: product.name || "",
        price: parseFloat(product.price) || 0,
        taxed_price: parseFloat(product.taxed_price) || 0,
        principal_image: Array.isArray(product.principal_image)
          ? product.principal_image
          : [],
        description: product.description || "",
        category: product.category || "",
        presentation: product.presentation || "",
        min_sale_unit: product.min_sale_unit || 0,
        units_per_package: product.units_per_package || 0,
        category_id: product.category_id || undefined,
        comission_percentage: product.comission_percentage || 0,
        price_per_m2: product.price_per_m2
          ? parseFloat(product.price_per_m2)
          : null,
        is_featured: Boolean(product.is_featured),
        // Extended fields for panel/table
        id: product.product_id?.toString() || "",
        brand: product.brand || "",
        material: product.material || "",
        line: product.line || "",
        color: product.color || "",
        color_hex_code: product.color_hex_code || undefined,
        units_available: product.units_available || 0,
        width: product.dimensions?.width ?? undefined,
        height: product.dimensions?.height ?? undefined,
        length: product.dimensions?.length ?? undefined,
        depth: product.dimensions?.depth ?? null,
        thickness: product.dimensions?.thickness ?? null,
        diameter: product.dimensions?.diameter ?? null,
        delivery_time: product.delivery_time || 0,
        warranty: product.warranty || 0,
        is_warranty_in_months: Boolean(product.is_warranty_in_months),
        unit_of_measure: product.unit_of_measure || "",
        packing_length: product.packing_length || 0,
        packing_width: product.packing_width || 0,
        packing_height: product.packing_height || 0,
        packing_weight: product.packing_weight || 0,
        is_with_iva: Boolean(product.is_with_iva),
        ambient_images: Array.isArray(product.ambient_images)
          ? product.ambient_images
          : [],
        technical_sheet: Array.isArray(product.technical_sheet)
          ? product.technical_sheet
          : [],
        status: getProductStatus(product.units_available),
      }));
      setData(mappedProducts);
    } else {
      setData([]);
    }
  }, [productsData]);

  const filteredData = data.filter((product) => {
    const matchesSearch =
      !searchQuery ||
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.id.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = !filters.status || product.status === filters.status;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className={styles.container}>
      <MaterialTable
        title=""
        columns={[
          {
            title: "Estado",
            field: "status",
            render: (rowData: SalesProduct) => (
              <ProductStatusBadge status={rowData.status} />
            ),
            cellStyle: {
              whiteSpace: "nowrap",
            },
          },
          {
            title: "Categoría",
            field: "category",
            render: (rowData: SalesProduct) => (
              <span className={styles.categoryBadge}>{rowData.category}</span>
            ),
            cellStyle: {
              whiteSpace: "nowrap",
            },
          },
          {
            title: "Línea",
            field: "line",
            render: (rowData: SalesProduct) => (
              <span className={styles.lineBadge}>{rowData.line}</span>
            ),
            cellStyle: {
              whiteSpace: "nowrap",
            },
          },
          {
            title: "Referencia",
            field: "name",
            render: (rowData: SalesProduct) => (
              <div>
                <div className={styles.productInfo}>{rowData.name}</div>
                <div className={styles.productId}>ID: {rowData.id}</div>
              </div>
            ),
          },
          {
            title: "Marca",
            field: "brand",
            render: (rowData: SalesProduct) => (
              <span className={styles.stockText}>{rowData.brand}</span>
            ),
          },
          {
            title: "Disponibles",
            field: "units_available",
            type: "numeric",
            render: (rowData: SalesProduct) => (
              <span
                className={`${styles.stockText} ${
                  rowData.units_available === 0
                    ? styles.stockZero
                    : rowData.units_available < 10
                    ? styles.stockLow
                    : styles.stockNormal
                }`}
              >
                {rowData.units_available}
              </span>
            ),
          },
          {
            title: "Precio sin IVA",
            field: "price",
            type: "numeric",
            render: (rowData: SalesProduct) => (
              <span className={styles.stockText}>
                {formatNumberToPrice(rowData.price)}
              </span>
            ),
          },
          {
            title: "Dimensiones",
            field: "dimensiones",
            render: (rowData: SalesProduct) => (
              <div className={styles.dimensions}>
                <span>
                  {rowData.width}m × {rowData.length}m
                </span>
              </div>
            ),
            cellStyle: {
              textAlign: "center",
            },
          },
        ]}
        data={filteredData}
        isLoading={isLoading}
        actions={[
          {
            icon: () => <Eye className={styles.actionIcon} />,
            tooltip: "Ver detalles",
            onClick: (_event, rowData) => {
              const product = rowData as SalesProduct;
              setSelectedProduct(product);
              setIsDetailsOpen(true);
            },
          },
        ]}
        options={{
          search: false,
          toolbar: false,
          paging: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          actionsColumnIndex: -1,
          padding: "dense",
          headerStyle: {
            backgroundColor: "#f4f6f8",
            color: "#637381",
            fontSize: "14px",
            fontWeight: 600,
            height: "3.5rem",
          },
          rowStyle: {
            fontSize: "14px",
            fontWeight: 400,
            color: "#1C252E",
          },
        }}
        localization={materialTableES}
      />

      <ProductDetails
        product={selectedProduct}
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
      />
    </div>
  );
}
